/**
 * ForManagersPage — the public /for/managers archetype landing page.
 *
 * Ported from the locked design design/mockups/V1_Landing_For_Managers_v1.html
 * (Template 2 instance, source: V1_Archetype_Landing_Pages.md Page 2).
 *
 * The app shell's <TopNav> renders the nav globally; this page is the
 * mockup content below <nav class="marketing-nav"> plus the canonical
 * footer. Scoped <style> block (media queries can't live in inline
 * styles); colors alias onto locked --ppr-* tokens. Unique class prefix
 * ppr-managers- so the global <style> never collides with sibling
 * marketing pages.
 *
 * Indexable page — usePageMeta only, no useNoindex.
 */

import { Link } from "react-router-dom";
import { usePageMeta } from "../../lib/use-page-meta.js";

const MANAGERS_DESCRIPTION =
  "You were promoted for your work, not for managing people. PeoplePrints gives you a tactical read before every performance review, 1-on-1, and hard conversation. From $149/month.";

const FAQS = [
  {
    q: "I'm a first-time manager. Is this too advanced for me?",
    a: "No — it's built for exactly this. PeoplePrints gives a first-time manager the read an experienced manager has built over years, before the conversation, in plain language.",
  },
  {
    q: "Do my reports have to do anything?",
    a: "No. The Profile is generated from minimal common data points. Your reports don't take a test and don't have to participate.",
  },
  {
    q: "Will the Blueprint tell me what to say word for word?",
    a: "It gives you a calibrated sequence and tactical guidance — what to lead with, what to expect, what to avoid. You run the conversation; the Blueprint makes sure you walk in prepared.",
  },
  {
    q: "Is this a performance prediction?",
    a: "No. It tells you how to deliver the message about performance. It does not predict performance, and it is not a substitute for your documented review process.",
  },
  {
    q: "What does it cost?",
    a: "The Manager tier is $149/month for up to 75 Profiles.",
  },
];

// FAQPage structured data — derived from FAQS so the schema text stays
// verbatim-matched to the on-page FAQ (a Google requirement for FAQ rich
// results). Mirrors the locked pattern in PricingPage.jsx.
const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FOOTER_LINKS = [
  { to: "/#how-it-works", label: "Product" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/for/recruiters", label: "For Recruiters" },
  { to: "/for/managers", label: "For Managers" },
  { to: "/for/hr-directors", label: "For HR Directors" },
  { to: "/for/founders", label: "For Founders" },
  { to: "/for/employee-relations", label: "For Employee Relations" },
  { to: "/methodology", label: "Methodology" },
  { to: "/about", label: "About" },
  { to: "/security-and-privacy", label: "Security & Privacy" },
  { to: "/demo", label: "Request a Demo" },
];

export default function ForManagersPage() {
  usePageMeta({
    title:
      "Tools for the Conversations Nobody Trained You For — PeoplePrints for Managers",
    description: MANAGERS_DESCRIPTION,
    canonicalPath: "/for/managers",
  });

  return (
    <div className="ppr-managers-root">
      <style>{MANAGERS_CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      {/* ── BLOCK 2 — HERO ── */}
      <section className="ppr-managers-hero">
        <div className="ppr-managers-hero-inner">
          <p className="ppr-managers-hero-eyebrow">
            For people managers &amp; team leads
          </p>
          <h1 className="ppr-managers-hero-h1">
            You were promoted for your work. Nobody trained you for the
            conversations.
          </h1>
          <p className="ppr-managers-hero-sub">
            The performance review where you have to deliver a hard message.
            The 1-on-1 with the report you can't quite read. The
            cross-functional partner you need to influence without authority.
            PeoplePrints gives you the read before each one — so the
            conversation goes the way you intended.
          </p>
        </div>
      </section>

      {/* ── BLOCK 3 — EXTRACTABLE DEFINITION ── */}
      <section className="ppr-managers-block ppr-managers-compact">
        <div className="ppr-managers-block-inner">
          <div className="ppr-managers-definition">
            <p className="ppr-managers-def-label">
              What PeoplePrints does for managers
            </p>
            <p className="ppr-managers-def-text">
              PeoplePrints is a pre-interaction intelligence tool for people
              managers. It generates a{" "}
              <strong>Predisposition Profile</strong> for each person a manager
              works with — direct reports, peers, cross-functional partners —
              and converts that Profile into a Blueprint for the specific
              conversation ahead: a Performance Review Blueprint, an Operating
              Manual Blueprint, or a Relational Blueprint. The manager walks
              into the conversation knowing how the person tends to receive
              information, what tends to land, and what tends to push them away.
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOCK 4 — THE PAIN ── */}
      <section className="ppr-managers-block ppr-managers-compact">
        <div className="ppr-managers-block-inner">
          <p className="ppr-managers-block-eyebrow">Sound familiar?</p>
          <h2 className="ppr-managers-block-h2">
            The job changed, and none of it came with a manual.
          </h2>
          <p className="ppr-managers-pain-quote">
            You were the best individual contributor on the team, so you got
            promoted. Then the job changed. Now your week is performance
            conversations, difficult feedback, a report who's disengaging, a
            peer in another function you need a yes from. None of it was in the
            job you were good at, and none of it came with a manual.
          </p>
        </div>
      </section>

      {/* ── BLOCK 5 — THE SHIFT ── */}
      <section className="ppr-managers-block ppr-managers-compact ppr-managers-tinted">
        <div className="ppr-managers-block-inner">
          <p className="ppr-managers-block-eyebrow">The shift</p>
          <h2 className="ppr-managers-block-h2">
            Every hard conversation comes with a read.
          </h2>
          <p className="ppr-managers-block-body">
            With PeoplePrints, every hard conversation comes with a read. The
            Performance Review Blueprint tells you how to calibrate a difficult
            message so it lands as honest rather than as an ambush. The
            Operating Manual gives you the one-pager on each report you wish HR
            had handed you. The Relational Blueprint gives you the read on the
            cross-functional partner before you ask for the thing you need.
          </p>
        </div>
      </section>

      {/* ── BLOCK 6 — WHAT YOU GET (In-This-Blueprint card grammar) ── */}
      <section className="ppr-managers-block ppr-managers-compact">
        <div className="ppr-managers-block-inner">
          <h2 className="ppr-managers-block-h2">
            The Blueprints that matter to a manager.
          </h2>
          <div className="ppr-managers-itb-card">
            <div className="ppr-managers-itb-stripe" />
            <div className="ppr-managers-itb-inner">
              <p className="ppr-managers-itb-label">What you get</p>
              <ol className="ppr-managers-itb-list">
                <li>
                  <strong>
                    A Predisposition Profile for everyone you manage or work
                    with
                  </strong>{" "}
                  — reports, peers, cross-functional partners.
                </li>
                <li>
                  <strong>The Performance Review Blueprint</strong> — calibrate
                  the difficult message to the person.
                </li>
                <li>
                  <strong>The Operating Manual Blueprint</strong> — the
                  one-pager on each report: how they decide, what motivates
                  them, how to give them feedback.
                </li>
                <li>
                  <strong>The Relational Blueprint</strong> — the read on the
                  people you influence without authority.
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 7 — HOW IT WORKS ── */}
      <section className="ppr-managers-block ppr-managers-compact ppr-managers-tinted">
        <div className="ppr-managers-block-inner">
          <p className="ppr-managers-block-eyebrow">How it works</p>
          <h2 className="ppr-managers-block-h2">
            Minimal data in. Profile. Blueprint.
          </h2>
          <div className="ppr-managers-step-list">
            <div className="ppr-managers-step-row">
              <div className="ppr-managers-step-num">1</div>
              <p className="ppr-managers-step-text">
                <strong>Enter the person's common data points.</strong>
              </p>
            </div>
            <div className="ppr-managers-step-row">
              <div className="ppr-managers-step-num">2</div>
              <p className="ppr-managers-step-text">
                <strong>The engine generates the Predisposition Profile.</strong>
              </p>
            </div>
            <div className="ppr-managers-step-row">
              <div className="ppr-managers-step-num">3</div>
              <p className="ppr-managers-step-text">
                <strong>
                  Open the Blueprint for the conversation you have coming up.
                </strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 8 — PRICING POINTER ── */}
      <section className="ppr-managers-block ppr-managers-compact">
        <div className="ppr-managers-block-inner">
          <div className="ppr-managers-price-pointer">
            <p className="ppr-managers-pp-tier">Manager — $149 / month</p>
            <p className="ppr-managers-pp-body">
              The Manager tier is built for you: 75 Profiles per month,
              Performance Review + Operating Manual + Relational Blueprints,
              everything in the Recruiter tier — <strong>$149/month</strong>.
            </p>
          </div>
          <p className="ppr-managers-xlink">
            Own the full HR surface, not just your team? See{" "}
            <Link to="/for/hr-directors">
              PeoplePrints for HR Directors →
            </Link>
          </p>
        </div>
      </section>

      {/* ── BLOCK 9 — FAQ ── */}
      <section className="ppr-managers-block ppr-managers-compact ppr-managers-tinted">
        <div className="ppr-managers-block-inner">
          <p className="ppr-managers-block-eyebrow">FAQ</p>
          <h2 className="ppr-managers-block-h2">Questions managers ask.</h2>
          <div className="ppr-managers-faq-list">
            {FAQS.map((f) => (
              <div className="ppr-managers-faq-item" key={f.q}>
                <h3 className="ppr-managers-faq-q">{f.q}</h3>
                <p className="ppr-managers-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOCK 10 — CREDENTIALING STRIP ── */}
      <section className="ppr-managers-cred-strip">
        <div className="ppr-managers-cred-strip-inner">
          <span className="ppr-managers-cs-label">The engine</span>
          PeoplePrints is built on a proprietary behavioral-prediction engine.
          The full methodology is at{" "}
          <Link to="/methodology">/methodology</Link>.
        </div>
      </section>

      {/* ── BLOCK 11 — CTA ── */}
      <section className="ppr-managers-cta-band">
        <div className="ppr-managers-cta-band-inner">
          <h2 className="ppr-managers-cta-band-h">
            Walk into the next hard conversation with the read done.
          </h2>
          <div className="ppr-managers-cta-actions">
            <Link
              to="/demo"
              className="ppr-managers-cta-btn ppr-managers-cta-primary"
            >
              Request a demo
            </Link>
            <Link
              to="/demo"
              className="ppr-managers-cta-btn ppr-managers-cta-secondary"
            >
              Book a 30-minute demo
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER — canonical site-wide footer ── */}
      <footer className="ppr-managers-footer">
        <div className="ppr-managers-footer-inner">
          <div className="ppr-managers-footer-links">
            {FOOTER_LINKS.map((l) => (
              <Link to={l.to} key={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="ppr-managers-footer-legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <p className="ppr-managers-footer-copy">
            © 2026 PeoplePrints™. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

const MANAGERS_CSS = `
.ppr-managers-root {
  --c-navy: var(--ppr-color-accent-navy);
  --c-steel: var(--ppr-color-accent-steel);
  --c-steel-dark: var(--ppr-color-accent-steel-dark);
  --c-powder: var(--ppr-color-accent-powder);
  --c-pale: var(--ppr-color-accent-pale);
  --c-pale-soft: var(--ppr-color-accent-pale-soft);
  --c-coral: var(--ppr-color-accent-coral);
  --c-coral-dark: var(--ppr-color-accent-coral-dark);
  --c-coral-very-soft: var(--ppr-color-accent-coral-very-soft);
  /* --c-gold: the locked marketing mockups use a gold hero-eyebrow accent
     with no token in design-tokens.v2.json — ported as a literal, flagged
     for design. */
  --c-gold: #C99A4B;
  --c-surface: var(--ppr-color-background-surface);
  --c-text: var(--ppr-color-text-primary);
  --c-text-2: var(--ppr-color-text-secondary);
  --c-muted: var(--ppr-color-text-muted);
  --c-border: var(--ppr-color-border-default);
  --f-display: var(--ppr-typography-font-family-display);
  --f-body: var(--ppr-typography-font-family-body);
  --f-ui: var(--ppr-typography-font-family-ui);
  --f-mono: var(--ppr-typography-font-family-mono);
  font-family: var(--f-body);
  color: var(--c-text);
  line-height: 1.55;
  background: var(--c-surface);
}
.ppr-managers-root * { box-sizing: border-box; }

.ppr-managers-block { padding: 64px 32px; }
.ppr-managers-block.ppr-managers-compact { padding: 44px 32px; }
.ppr-managers-block.ppr-managers-tinted { background: var(--c-pale-soft); }
.ppr-managers-block-inner { max-width: 940px; margin: 0 auto; }
.ppr-managers-block-eyebrow { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 14px; }
.ppr-managers-block-h2 { font-family: var(--f-display); font-size: 27px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 16px; }
.ppr-managers-block-body { font-family: var(--f-body); font-size: 16.5px; line-height: 1.65; color: var(--c-text); margin: 0 0 16px; }
.ppr-managers-block-body strong { color: var(--c-navy); }

.ppr-managers-hero { padding: 80px 32px 56px; background: linear-gradient(180deg, var(--c-pale-soft) 0%, var(--c-surface) 100%); }
.ppr-managers-hero-inner { max-width: 900px; margin: 0 auto; text-align: center; }
.ppr-managers-hero-eyebrow { font-family: var(--f-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--c-gold); margin: 0 0 18px; }
.ppr-managers-hero-h1 { font-family: var(--f-display); font-size: 40px; line-height: 1.1; letter-spacing: -0.025em; color: var(--c-navy); margin: 0 0 20px; }
.ppr-managers-hero-sub { font-family: var(--f-body); font-size: 18px; line-height: 1.65; color: var(--c-text-2); margin: 0 auto; max-width: 720px; text-align: left; }

.ppr-managers-definition { background: var(--c-pale-soft); border: 1.5px solid var(--c-powder); border-left: 6px solid var(--c-navy); padding: 26px 30px; }
.ppr-managers-def-label { font-family: var(--f-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--c-steel-dark); margin: 0 0 10px; }
.ppr-managers-def-text { font-family: var(--f-body); font-size: 17px; line-height: 1.65; color: var(--c-text); margin: 0; }
.ppr-managers-def-text strong { color: var(--c-navy); }

.ppr-managers-pain-quote { font-family: var(--f-body); font-size: 17px; font-style: italic; line-height: 1.6; color: var(--c-text); border-left: 4px solid var(--c-coral); background: var(--c-coral-very-soft); padding: 20px 24px; margin: 0; }

.ppr-managers-itb-card { background: var(--c-surface); border: 3px solid var(--c-navy); margin: 6px 0 0; }
.ppr-managers-itb-stripe { height: 8px; background: var(--c-coral); }
.ppr-managers-itb-inner { padding: 24px 28px; }
.ppr-managers-itb-label { font-family: var(--f-display); font-size: 19px; color: var(--c-navy); text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 14px; }
.ppr-managers-itb-list { margin: 0; padding: 0; list-style: none; counter-reset: itb; }
.ppr-managers-itb-list li { position: relative; padding: 11px 0 11px 38px; font-family: var(--f-body); font-size: 15.5px; line-height: 1.55; border-bottom: 1px solid var(--c-border); counter-increment: itb; }
.ppr-managers-itb-list li:last-child { border-bottom: none; padding-bottom: 0; }
.ppr-managers-itb-list li::before { content: counter(itb); position: absolute; left: 0; top: 10px; width: 23px; height: 23px; background: var(--c-navy); color: #FFF; border-radius: 50%; font-family: var(--f-display); font-size: 11px; display: flex; align-items: center; justify-content: center; }
.ppr-managers-itb-list strong { color: var(--c-navy); }

.ppr-managers-step-list { margin: 20px 0 0; }
.ppr-managers-step-row { display: grid; grid-template-columns: 40px 1fr; gap: 18px; padding: 15px 0; border-bottom: 1px solid var(--c-border); }
.ppr-managers-step-row:last-child { border-bottom: none; }
.ppr-managers-step-num { width: 32px; height: 32px; border-radius: 50%; background: var(--c-navy); color: #FFF; font-family: var(--f-display); font-size: 13px; display: flex; align-items: center; justify-content: center; }
.ppr-managers-step-text { font-family: var(--f-body); font-size: 15.5px; line-height: 1.6; color: var(--c-text); margin: 4px 0 0; }
.ppr-managers-step-text strong { color: var(--c-navy); }

.ppr-managers-price-pointer { border: 2px solid var(--c-navy); border-top: 8px solid var(--c-coral); background: var(--c-surface); padding: 26px 30px; }
.ppr-managers-pp-tier { font-family: var(--f-display); font-size: 13px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 8px; }
.ppr-managers-pp-body { font-family: var(--f-body); font-size: 16px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-managers-pp-body strong { color: var(--c-navy); }

.ppr-managers-xlink { font-family: var(--f-ui); font-size: 14px; color: var(--c-text-2); margin: 18px 0 0; }
.ppr-managers-xlink a { color: var(--c-coral-dark); font-weight: 600; text-decoration: none; }

.ppr-managers-faq-list { margin: 20px 0 0; }
.ppr-managers-faq-item { border: 1.5px solid var(--c-border); border-left: 4px solid var(--c-steel); margin: 0 0 12px; background: var(--c-surface); padding: 20px 24px; }
.ppr-managers-faq-q { font-family: var(--f-display); font-size: 15.5px; color: var(--c-navy); margin: 0 0 8px; letter-spacing: -0.005em; }
.ppr-managers-faq-a { font-family: var(--f-body); font-size: 15px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-managers-faq-a strong { color: var(--c-navy); }

.ppr-managers-cred-strip { background: var(--c-navy); color: #FFF; padding: 28px 32px; }
.ppr-managers-cred-strip-inner { max-width: 940px; margin: 0 auto; font-family: var(--f-body); font-size: 14.5px; line-height: 1.65; }
.ppr-managers-cred-strip-inner a { color: var(--c-powder); text-decoration: underline; }
.ppr-managers-cs-label { font-family: var(--f-display); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-powder); display: block; margin: 0 0 8px; }

.ppr-managers-cta-band { background: var(--c-pale-soft); padding: 60px 32px; text-align: center; }
.ppr-managers-cta-band-inner { max-width: 760px; margin: 0 auto; }
.ppr-managers-cta-band-h { font-family: var(--f-display); font-size: 24px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 24px; }
.ppr-managers-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
.ppr-managers-cta-btn { display: inline-block; padding: 14px 26px; border-radius: 6px; text-decoration: none; font-family: var(--f-display); font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; }
.ppr-managers-cta-primary { background: var(--c-coral); color: #FFF; border: 2px solid var(--c-coral); }
.ppr-managers-cta-secondary { background: transparent; color: var(--c-navy); border: 2px solid var(--c-navy); }

.ppr-managers-footer { background: var(--c-surface); border-top: 1px solid var(--c-border); padding: 44px 32px; }
.ppr-managers-footer-inner { max-width: 1080px; margin: 0 auto; text-align: center; }
.ppr-managers-footer-links { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin: 0 0 14px; font-family: var(--f-ui); font-size: 13px; font-weight: 600; }
.ppr-managers-footer-links a { color: var(--c-text-2); text-decoration: none; }
.ppr-managers-footer-legal { display: flex; gap: 16px; justify-content: center; margin: 0 0 16px; font-family: var(--f-ui); font-size: 12.5px; }
.ppr-managers-footer-legal a { color: var(--c-muted); text-decoration: none; }
.ppr-managers-footer-copy { font-family: var(--f-mono); font-size: 11.5px; color: var(--c-muted); }

@media (max-width: 800px) {
  .ppr-managers-hero-h1 { font-size: 30px; }
  .ppr-managers-block-h2 { font-size: 22px; }
}
`;
